import model from './model';
import { PopulatedMenuClient } from '../../api/PopulatedMenuClient';
import { OLOController } from './oloController';
import type { CartLineItem } from '../../services/cartService';
import { CartService } from '../../services/cartService';
import { context } from '../../context/RootContext';
import type { Cart } from '@wix/ambassador-ecom-v1-cart/types';
import type { PopulatedMenu } from '../../types/menusTypes';
import { WarmupDataManager } from '../../utils/WarmupDataManager';
import { FedopsLogger } from '../../utils/monitoring/FedopsLogger';
import { ModalService } from '../../services/modalService';
import { OperationsClient } from '../../api/operationClient';
import type { Experiments, TFunction } from '@wix/yoshi-flow-editor';
import type { ItemData, Item } from '../../types/item';
import { SPECS } from '../../appConsts/experiments';
import { getCartItemById } from '../../utils/cartUtils';
import type { IBIReporterService, OperationDurationParams } from '../../services/biReporterService';
import { BIReporterService } from '../../services/biReporterService';
import type { DispatchInfo, DispatchType, Operation } from '../../types/businessTypes';
import { PersistDataService } from 'root/services/persistDataService';
import { getSiteCurrency, getSiteLanguage, getSiteLocale } from '../../utils/siteDataUtils';
import { PriceFormattingConverter } from '@wix/restaurants-olo-operations-client-commons';
import { initDispatchState } from 'root/states/initDispatchState';
import { FulfillmentsClient } from '../../api/fulfillmentsClient';
import { dispatchState } from 'root/states/DispatchState';
import { getMonitoredApiCall } from 'root/api/utils/getMonitoredApiCall';
import { DEFAULT_TIMEZONE } from 'root/api/consts';
import type { SortableMenu } from './panels/MenuSettings/types';
import type { GetMenuIdsByOperation } from 'root/api/PopulateMenuIdsByOperationClient';
import { PopulateMenuIdsByOperationClient } from 'root/api/PopulateMenuIdsByOperationClient';
import { OrdersSettingsService } from 'root/services/ordersSettingsService';
import { menusState } from 'root/states/MenusState';
import { sectionsState } from 'root/states/SectionsState';
import {
  onItemModalClosed,
  openDispatchModal,
  openItemModal,
  openItemOrDispatchModal,
} from 'root/utils/utils';
import { getTimezoneOffset } from 'root/api/utils/utils';
import { generateBreadcrumbsSD } from 'root/utils/seoUtils';
import { getSiteStructure } from 'root/utils/siteStructure';
import { cartState } from 'root/states/cartState';
import { itemState } from 'root/states/itemsState';
import { getPageOperationId } from 'root/utils/pageOperationUtils';
import { autorun } from 'mobx';
import { registerToUrlChange } from 'root/utils/navigationUtils';
import {
  getOperationBIEventSettings,
  getOperationDurationParamsByOperationId,
} from 'root/utils/BIDurationUtils';
import { withBIDuration } from 'root/utils/withBIDuration';

type ImageSD = {
  url: string;
  id: string;
  height: number;
  width: number;
  altText: string;
};

const reportConductedExperiments = (bi: IBIReporterService, experiments: Experiments) => {
  const value = experiments.all();
  bi.reportOloGenericDebugBiEvent({
    subjectType: 'conductedExperiments',
    value,
  });
};

export default model.createController(({ $w, $widget, flowAPI }) => {
  const {
    translations,
    httpClient,
    errorHandler,
    experiments,
    bi,
    panoramaClient,
    environment,
    controllerConfig,
    reportError,
  } = flowAPI;
  const { wixCodeApi, platformAPIs, compId } = controllerConfig;
  const t = translations.t as TFunction;
  const { metaSiteId = '' } = platformAPIs.bi || {};
  const timezone = wixCodeApi.site.timezone || DEFAULT_TIMEZONE;
  const locale = getSiteLocale(flowAPI);
  const language = getSiteLanguage(flowAPI);
  const errorMonitor = panoramaClient?.errorMonitor();

  const enableMultiLocation = experiments.enabled(SPECS.enableMultiLocation);
  const isMemberLoggedIn = !!controllerConfig.wixCodeApi.user.currentUser?.loggedIn;
  const useCalculateFirstAvailableAPI = experiments.enabled(SPECS.calculateFirstAvailableTimeSlot);
  const enableSectionItemAvailability = experiments.enabled(SPECS.sectionItemAvailability);

  $widget.onPropsChanged(async (prevProps, nextProps) => {
    if (
      prevProps.showUnavailableMenus !== nextProps.showUnavailableMenus ||
      prevProps.showExpiredMenus !== nextProps.showExpiredMenus ||
      prevProps.unavailableMenusShownLast !== nextProps.unavailableMenusShownLast
    ) {
      menusState.setAvailabilitySettings({
        showUnavailableMenus: nextProps.showUnavailableMenus,
        showExpiredMenus: nextProps.showExpiredMenus,
        unavailableMenusShownLast: nextProps.unavailableMenusShownLast,
      });
    }
    if (prevProps.sortedMenus !== nextProps.sortedMenus) {
      const menusOrder = (
        (nextProps.sortedMenus || menusState.sortedMenusDto) as SortableMenu[]
      ).map((sortedMenu) => sortedMenu.id);
      menusState.orderMenus(menusOrder);
    }
  });

  const warmupData = new WarmupDataManager(wixCodeApi.window.warmupData, environment.isSSR);

  context.biReporterService = BIReporterService({
    biLogger: bi,
    environment,
    widgetInstanceId: compId,
  });
  context.fedopsLogger = new FedopsLogger(panoramaClient, metaSiteId, context.biReporterService);
  const { fedopsLogger, biReporterService } = context;
  context.CartService = CartService({
    httpClient,
    errorHandler,
    fedopsLogger,
    experiments,
    metaSiteId: platformAPIs.bi?.metaSiteId,
    wixAPI: wixCodeApi,
    biReporterService,
    errorMonitor,
    isEditor: environment.isEditor,
    isSSR: environment.isSSR,
  });

  menusState.setOrdersSettingsService(
    OrdersSettingsService({
      httpClient,
      fedopsLogger,
      biReporterService,
      reportError,
      errorHandler,
      useCalculateFirstAvailableAPI,
    })
  );

  context.PersistDataService = PersistDataService(
    platformAPIs.storage.session,
    environment.isEditor
  );

  context.ModalService = context.ModalService
    ? context.ModalService
    : new ModalService(wixCodeApi, fedopsLogger, environment.isMobile, context.CartService);

  const oloController = new OLOController($w, t);

  fedopsLogger.loadOloPageStarted();
  biReporterService?.reportRestaurantsUouPageStartedLoadingBiEvent();

  const currency = getSiteCurrency(flowAPI);
  context.priceFormatter = PriceFormattingConverter.createPriceFormatter(locale, currency);
  context.currency = currency;

  const operationsClient = new OperationsClient(
    flowAPI.httpClient,
    flowAPI.errorHandler,
    biReporterService
  );

  const pageOperationIdPromise = getPageOperationId(wixCodeApi.site, errorMonitor);

  reportConductedExperiments(biReporterService, experiments);

  const fetchOperations = async () => {
    const pageId = await pageOperationIdPromise;
    if (!pageId) {
      errorMonitor?.reportError(new Error('operationGroupId is missing on the page'));
      context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
      return [];
    }

    const operations = await operationsClient.getOperationsByGroupId(pageOperationIdPromise);
    if (operations?.length) {
      const validOperations = operations.filter((o) => !(o.locationId && !o.locationDetails));
      return validOperations;
    } else {
      errorMonitor?.reportError(new Error(`pageId is not valid operationGroupId = ${pageId}`));
    }

    const operation = await operationsClient.getOperation(pageOperationIdPromise);
    if (!operation) {
      errorMonitor?.reportError(new Error(`pageId is not valid operationId = ${pageId}`));
      context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
      return [];
    }
    return [operation];
  };

  const populatedMenuClient = PopulatedMenuClient({
    httpClient,
    errorHandler,
    msid: metaSiteId,
    currency,
    timezone,
    enableSectionItemAvailability,
  });

  const getMonitoredPopulatedMenuClient = (menuIds: Promise<GetMenuIdsByOperation | undefined>) => {
    const fetchMenusByOperation = () => populatedMenuClient.getPartial(menuIds);
    return getMonitoredApiCall<PopulatedMenu[] | undefined, OperationDurationParams>({
      operationDurationParams: {
        ...baseOperationDurationParams.operationDurationParams,
        useSSRCache: false,
      },
      name: 'fetchMenusByOperation',
      callback: fetchMenusByOperation,
      fedops: { start: fedopsLogger.fetchMenusDataStarted, end: fedopsLogger.fetchMenusDataEnded },
      reportError,
      onError: () => {
        context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
      },
    });
  };

  const baseOperationDurationParams = {
    operationDurationBIEvent: biReporterService?.reportOperationDurationBiEvent,
    operationDurationParams: { isSSRCall: environment.isSSR },
  };

  const getMonitoredOperationClient = () =>
    getMonitoredApiCall<Operation[], OperationDurationParams>({
      ...baseOperationDurationParams,
      name: 'fetchOperations',
      callback: fetchOperations,
      operationDurationParams: {
        useSSRCache: true,
        ...baseOperationDurationParams.operationDurationParams,
      },
      fedops: {
        start: fedopsLogger.fetchOperationDataStarted,
        end: fedopsLogger.fetchOperationDataEnded,
      },

      reportError,

      onError: () => {
        context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
      },
    });

  const operationsPromise = warmupData
    .manageData<{ data?: Operation[]; error?: Error } | undefined>(
      getMonitoredOperationClient,
      'operations',
      pageOperationIdPromise
    )
    .then((response) => response?.data);

  const currentOperationIdPromise = getCurrentOperationId();
  const truncateMenuItemsOnEditorToValue = experiments.get(SPECS.truncateMenuItemsOnEditorToValue);
  const isTruncateMenuItemsOnEditor = !isNaN(truncateMenuItemsOnEditorToValue);
  const LAZY_LOAD_WITHOUT_TRUNCATE = 'all';
  const isLazyLoadMenusOnEditor =
    isTruncateMenuItemsOnEditor || truncateMenuItemsOnEditorToValue === LAZY_LOAD_WITHOUT_TRUNCATE;

  const setSortedMenus = (menus: PopulatedMenu[]) => {
    const menusOrder = (($widget.props.sortedMenus || menus) as SortableMenu[] | undefined)?.map(
      (sortedMenu) => sortedMenu.id
    );

    menusState.setAvailabilitySettings({
      showUnavailableMenus: $widget.props.showUnavailableMenus,
      showExpiredMenus: $widget.props.showExpiredMenus,
      unavailableMenusShownLast: $widget.props.unavailableMenusShownLast,
    });

    menusState.setMenus(
      menus,
      menusOrder,
      environment.isEditor && isTruncateMenuItemsOnEditor
        ? Number(truncateMenuItemsOnEditorToValue)
        : undefined
    );

    if (enableSectionItemAvailability) {
      const allSections = menus.flatMap((menu) => menu.sections);
      sectionsState.setSections(allSections);
    }
  };

  const menuIdsByOperationClient = PopulateMenuIdsByOperationClient({
    errorHandler,
    httpClient,
    fedopsLogger,
    errorMonitor,
    reportError,
    timezoneOffset: getTimezoneOffset(timezone),
    biReporterService,
    useCalculateFirstAvailableAPI,
  });

  const getPopulateMenuIdsByOperation = async (operationIdPromise: Promise<string>) => {
    const currentOperation = (await operationsPromise)?.find(
      async (o) => o.id === (await operationIdPromise)
    );

    const locationTimezone = currentOperation?.locationDetails?.timeZone;
    if (locationTimezone) {
      menuIdsByOperationClient.setTimezoneOffset(getTimezoneOffset(locationTimezone));
    }

    return warmupData
      .manageData<GetMenuIdsByOperation | undefined>(
        () => menuIdsByOperationClient.getAll(operationIdPromise),
        'menuIdsByOperation',
        operationIdPromise
      )
      .then((response) => {
        const menusAvailability = response?.menusAvailability || {};
        menusState.setMenusAvailability(menusAvailability);
        return response;
      });
  };

  const initMenus = async (operationId?: Promise<string>) => {
    const operationIdPromise = operationId ?? currentOperationIdPromise;
    const menuIdsByOperationPromise = getPopulateMenuIdsByOperation(operationIdPromise);
    const firstLoad = !operationId;
    itemState.setIsLoading(true);
    return warmupData
      .manageData<{ data?: PopulatedMenu[]; error?: Error } | undefined>(
        () => getMonitoredPopulatedMenuClient(menuIdsByOperationPromise),
        'populatedMenus',
        operationIdPromise
      )
      .then(async (response) => {
        const { data, error } = response || {};
        let itemPromise = Promise.resolve();
        if (data) {
          if (firstLoad) {
            setSortedMenus(data);
          }
          const _operationId = await operationIdPromise;
          const currentOperation = (await operationsPromise)?.find(
            async (o) => o.id === (await operationIdPromise)
          );
          const monitoredItemCall = () =>
            getMonitoredApiCall({
              ...baseOperationDurationParams,
              operationDurationParams: {
                ...getOperationDurationParamsByOperationId(_operationId, currentOperation),
                ...baseOperationDurationParams.operationDurationParams,
                useSSRCache: true,
                extraFields: {
                  menusQuantity: data.length,
                },
              },
              name: 'fetchPopulatedItems',
              callback: () => populatedMenuClient.getItems(data),
              reportError,
              errorMonitor,
              onError: () => {
                context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
              },
            });
          itemPromise = warmupData
            .manageData<{ data?: Item[]; error?: Error } | undefined>(
              monitoredItemCall,
              'fetchPopulatedItems',
              operationIdPromise
            )
            .then((res) => {
              const { data: items, error: fetchItemsError } = res ?? {};
              if (fetchItemsError) {
                menusState.setHasError(true);
              } else if (items) {
                const itemMap = items.reduce((acc, item) => {
                  acc.set(item.id, item);
                  return acc;
                }, new Map<string, Item>());
                if (!firstLoad) {
                  setSortedMenus(data);
                }
                itemState.setItemMap(itemMap);
                itemState.setItemsStateMap(items);
                itemState.setIsLoading(environment.isSSR);
              }
              itemState.setIsLoading(environment.isSSR);
            });
        } else {
          if (menusState.menus.length) {
            menusState.setMenus([]);
            if (enableSectionItemAvailability) {
              sectionsState.clearSections();
              itemState.clearItemsState();
            }
            oloController.setMenus();
          }
        }
        return { data, error, itemPromise };
      });
  };

  const menusPromise = initMenus();

  const cartPromise = context.CartService?.getCurrentCart().then((cart) => {
    initCartDetails(cart);
    return cart;
  });

  const getLocationIdByQueryParam = () => {
    const { locationId } = wixCodeApi.location.query || {};
    return locationId;
  };

  const setQueryParamLocationId = (locationId: string) => {
    wixCodeApi.location.queryParams.add({
      locationId,
    });
  };

  const getLocationIdFromCart = async () => (await cartPromise)?.businessLocationId;

  const getSelectedOperation = async (pageOperations?: Operation[]) => {
    if (enableMultiLocation) {
      const queryParamLocation = getLocationIdByQueryParam();
      const selectedLocationId = queryParamLocation ?? (await getLocationIdFromCart());
      if (selectedLocationId) {
        const selectedOperation = pageOperations?.find(
          (operation) => operation.locationId === selectedLocationId
        );
        return selectedOperation;
      }
    }
  };

  async function getCurrentOperationId(operations?: Operation[]): Promise<string> {
    if (dispatchState.currentOperationId) {
      return dispatchState.currentOperationId;
    }

    const pageOperations = operations ?? (await operationsPromise);
    if (!pageOperations?.length) {
      return '';
    }

    const isOperationGroupsExists = !!pageOperations?.[0]?.operationGroupId;
    if (!isOperationGroupsExists) {
      return pageOperations?.[0].id ?? '';
    }

    const currentOperation =
      (await getSelectedOperation(pageOperations)) ??
      pageOperations?.find((operation) => operation.locationDetails?.default) ??
      pageOperations?.[0];

    return currentOperation?.id ?? '';
  }

  const initDispatchStatePromise = new Promise<void>(async (resolve) => {
    const operationId = await currentOperationIdPromise;
    if (!operationId) {
      resolve();
      return;
    }
    const operations = (await operationsPromise) ?? [];
    const locationIds = enableMultiLocation
      ? Array.from(new Set(operations?.map((operation) => operation.locationId)))
      : [];
    const fulfillmentsClient = new FulfillmentsClient(
      httpClient,
      errorHandler,
      operations ?? [],
      operationId,
      useCalculateFirstAvailableAPI
    );
    const persistedState = context.PersistDataService?.getDispatchState(locationIds);
    const fetchDispatchState = async () =>
      operations &&
      operationId &&
      initDispatchState({
        fulfillmentsClient,
        operations,
        timezone,
        cart: await cartPromise,
        persistedState,
        fedopsLogger,
        reportError,
        errorMonitor,
        operationId,
        supportMultiLocation: enableMultiLocation,
      });

    const dispatches = await fetchDispatchState();
    dispatches &&
      dispatchState.init({
        dispatchStateByLocation: dispatches,
        operationId,
        operations,
        isLocationSelected: enableMultiLocation && !!(await getSelectedOperation(operations)),
        isLoading: true,
        isDataLoaded: true,
      });

    const configuredLocationId = dispatchState.currentOrFirstLocationByConfiguredDispatchTypes;
    if (configuredLocationId !== dispatchState.currentOperation?.locationId) {
      const currentOperationId = operations.find(
        (op) => op.locationId === configuredLocationId
      )?.id;
      if (currentOperationId) {
        currentOperationId && dispatchState.setCurrentOperationId(currentOperationId);
        dispatchState.setDispatchType(
          dispatchState.dispatchStateByCurrentLocation.selectedDispatchType
        );
        await init(currentOperationId);
      }
    }
    await menusState.updateAvailabilityStatus(
      dispatchState.currentOperationId,
      dispatchState.dispatchInfo,
      dispatchState.selectedDispatchType
    );
    if (!environment.isSSR) {
      dispatchState.setIsLoading(false);
    }
    resolve();
  });

  const initCartDetails = (cart?: Cart) => {
    if (!cart) {
      return;
    }
    const { lineItems } = cart;
    lineItems && cartState.setCartLineItems({ lineItems });
  };

  const retryOnEmptyMenus = async (counter = 0, operationId: string) => {
    if (counter < 5) {
      let resolveFn: (data?: PopulatedMenu[]) => void;
      const menusRetryPromise = new Promise<PopulatedMenu[] | undefined>(
        (resolve) => (resolveFn = resolve)
      );

      setTimeout(async () => {
        const { data } = (await initMenus(Promise.resolve(operationId))) || {};
        resolveFn(data);
      }, 2000);

      const menus = (await menusRetryPromise) || [];
      if (!menus?.length) {
        retryOnEmptyMenus(counter + 1, operationId);
      }
    }
  };

  const getItemById = (itemId: string, menus: PopulatedMenu[]) => {
    if (itemId) {
      for (const menu of menus) {
        for (const section of menu.sections) {
          const items = section.itemIds
            ?.filter((id) => itemState.itemMap.has(id) && id === itemId)
            .map((id) => ({
              item: itemState.itemMap.get(id),
              sectionId: section.id,
              menuId: menu.id,
            }));

          if (items?.length) {
            return items;
          }
        }
      }
    }
  };

  const openEditItemModal = async (cartItemId: string, menus: PopulatedMenu[]) => {
    const cartItem = getCartItemById(cartState.cartLineItems, cartItemId);
    openItemModalByItemId({ menus, cartItem });
  };

  const onDispatchModalSave = async ({
    dispatchType,
    dispatchInfo,
    operationId,
    isLocationChanged,
  }: {
    dispatchType: DispatchType;
    dispatchInfo: DispatchInfo;
    operationId: string;
    isLocationChanged: boolean;
  }) => {
    const isCurrentOperationChanged = dispatchState.operationId !== operationId;
    isCurrentOperationChanged && dispatchState.setCurrentOperationId(operationId);
    dispatchState.setIsLocationSelected(true);
    dispatchState.update(dispatchType, dispatchInfo);
    if (isLocationChanged) {
      await context.CartService?.clearCart();
    }
    context.CartService?.setShippingDetails(dispatchState.getShippingDetails());
    await menusState.updateAvailabilityStatus(
      dispatchState.currentOperationId,
      dispatchInfo,
      dispatchType
    );
  };

  const openItemModalByQueryParam = async (menus: PopulatedMenu[]) => {
    const { itemId, sectionId, menuId } = wixCodeApi.location.query ?? {};
    if (itemId && sectionId && menuId) {
      openItemModalByItemId({ menus, menuId, sectionId, itemId });
    }
  };

  const openDispatchModalAfterItemModal = async ({
    dishModalResponse,
    ghostMode,
    shouldOpenItemModalBeforeDispatch,
    itemData,
    menuId,
    sectionId,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dishModalResponse: any;
    ghostMode: boolean;
    shouldOpenItemModalBeforeDispatch: boolean;
    itemData: ItemData;
    menuId: string;
    sectionId: string;
  }) => {
    if (dishModalResponse?.additionaldata) {
      if (shouldOpenItemModalBeforeDispatch && !dispatchState.dispatchInfo.address && !ghostMode) {
        openDispatchModal({
          dispatchState: dispatchState.state,
          context,
          onSave: async ({ dispatchType, dispatchInfo, operationId, isLocationChanged }) => {
            const isCurrentOperationChanged = dispatchState.operationId !== operationId;
            if (!isCurrentOperationChanged) {
              const { additionaldata } = dishModalResponse ?? {};
              const res = context.CartService?.addItemToCart(
                additionaldata.cartLineItem,
                itemData?.id ?? undefined,
                dispatchState.currentOperation?.locationId
              );
              dishModalResponse.data = res;
              await onItemModalClosed(
                itemData,
                menuId,
                sectionId,
                context,
                t,
                dishModalResponse,
                wixCodeApi.site
              );
            }
            onDispatchModalSave({
              dispatchType,
              dispatchInfo,
              operationId,
              isLocationChanged,
            });
          },
        });
      } else {
        await onItemModalClosed(
          itemData,
          menuId,
          sectionId,
          context,
          t,
          dishModalResponse,
          wixCodeApi.site
        );
      }
    }
  };

  const openItemModalByItemId = async ({
    menus,
    menuId,
    sectionId,
    itemId,
    cartItem,
  }: {
    menus: PopulatedMenu[];
    menuId?: string;
    sectionId?: string;
    itemId?: string;
    cartItem?: CartLineItem;
  }) => {
    const shouldOpenItemModalBeforeDispatch = experiments.enabled(
      SPECS.openItemModalBeforeDispatch
    );

    const [menuItem] =
      (cartItem?.catalogItemId
        ? getItemById(cartItem?.catalogItemId, menus)
        : getItemById(itemId ?? '', menus)?.filter(
            (item) => item.menuId === menuId && item.sectionId === sectionId
          )) ?? [];

    const itemData = menuItem.item as ItemData;
    const menuItemId = menuItem.menuId;
    const sectionItemId = menuItem.sectionId;

    const dishModalResponse = await openItemOrDispatchModal({
      enableMultiLocation,
      shouldOpenItemModalBeforeDispatch,
      isMemberLoggedIn,
      itemData,
      menuId: menuItemId,
      sectionId: sectionItemId,
      cartItem,
      context,
      locale: language,
      timezone,
      t,
      onSave: async ({ dispatchType, dispatchInfo, operationId, isLocationChanged }) => {
        const isCurrentOperationChanged = dispatchState.operationId !== operationId;
        onDispatchModalSave({
          dispatchType,
          dispatchInfo,
          operationId,
          isLocationChanged,
        });

        if (!isCurrentOperationChanged) {
          const SHOULD_ADD_ITEM_TO_CART = true;
          openItemModal(
            context,
            itemData,
            menuItemId,
            sectionItemId,
            SHOULD_ADD_ITEM_TO_CART,
            language,
            timezone,
            t,
            onDispatchModalSave
          );
        }
      },
    });

    const ghostMode =
      enableMultiLocation && dispatchState.isMultiLocation && !dispatchState.isLocationSelected;
    openDispatchModalAfterItemModal({
      dishModalResponse,
      ghostMode,
      shouldOpenItemModalBeforeDispatch,
      itemData,
      menuId: menuItemId,
      sectionId: sectionItemId,
    });

    // await onItemModalClosed(itemData, menuItemId, sectionItemId, context, t, dishModalResponse);
  };

  const retryFetchingMenusIfNeeded = (menus: PopulatedMenu[], operationId: string) => {
    if (!menus.length) {
      // TODO: set menus empty state
      !environment.isViewer && retryOnEmptyMenus(0, operationId);
    }
  };

  const registerOnCartChangeEvent = () => {
    context.CartService?.onChange(async () => {
      const { lineItems: cartLineItems } = (await context.CartService?.getCurrentCart()) ?? {};
      cartLineItems && cartState.setCartLineItems({ lineItems: cartLineItems });
    });
  };

  const editCartItem = () => {
    const supportEditCartItem = experiments.enabled(SPECS.editCartItem);
    if (supportEditCartItem) {
      const { cartItemId = undefined } = controllerConfig.wixCodeApi.location.query || {};

      if (cartItemId) {
        controllerConfig.wixCodeApi.location.queryParams.remove(['cartItemId']);
        openEditItemModal(cartItemId, menusState.sortedMenusDto);
      }
    }
  };

  const openItemModalsIfNeeded = async (menus: PopulatedMenu[]) => {
    await openItemModalByQueryParam(menus);

    editCartItem();
  };

  const convertMenusToImageSD = (menus: PopulatedMenu[]): ImageSD[] =>
    menus
      .flatMap((menu) =>
        menu.sections.flatMap((section) =>
          section.itemIds
            ?.filter((itemId) => itemState.itemMap.has(itemId))
            ?.map((itemId) => {
              const item = itemState.itemMap.get(itemId);
              return {
                url: item?.image?.url ?? '',
                id: item?.id,
                height: item?.image?.height ?? 0,
                width: item?.image?.width ?? 0,
                altText: item?.image?.altText ?? item?.name,
              };
            })
        )
      )
      .filter(Boolean) as ImageSD[];

  const init = async (currentOperationId?: string) => {
    const operationId = currentOperationId ?? (await currentOperationIdPromise);

    try {
      const newMenuPromise = initMenus(Promise.resolve(operationId));
      const { itemPromise } = (await newMenuPromise) ?? {};
      retryFetchingMenusIfNeeded(menusState.sortedMenusDto, operationId);

      const oloLoadedPromise = Promise.all([itemPromise]).then(async () => {
        if (experiments.enabled(SPECS.seo)) {
          const itemData = { images: convertMenusToImageSD(menusState.sortedMenusDto) };
          wixCodeApi.seo.renderSEOTags({ itemType: 'IMAGES_COMPONENT', itemData });
        }
      });
      if (environment.isEditor) {
        await oloLoadedPromise;
      }
    } catch (e) {
      // TODO: set menus error state
      context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
      // eslint-disable-next-line no-console
      console.log('error', e);
    }
  };

  let initialLoad = true;

  return {
    pageReady: async () => {
      $widget.fireEvent('widgetLoaded', {});

      oloController.setMenus();
      try {
        const operationId =
          dispatchState.currentOperationId ?? (await currentOperationIdPromise) ?? '';

        if (!operationId) {
          console.error('operationId is missing');
          return;
        }
        dispatchState.setCurrentOperationId(operationId);

        const { itemPromise } = await withBIDuration({
          callback: () => menusPromise,
          ...getOperationBIEventSettings(menusState, itemState, biReporterService),
          name: 'initMenus',
        });

        retryFetchingMenusIfNeeded(menusState.sortedMenusDto, operationId);

        // Report breadcrumbs SD to seo
        const siteStructure = await getSiteStructure(wixCodeApi.site);
        const breadcrumbsSd = generateBreadcrumbsSD(flowAPI, siteStructure);
        await flowAPI.controllerConfig.wixCodeApi.seo.setStructuredData([breadcrumbsSd]);

        const initDispatchStateWithDurationPromise = withBIDuration({
          callback: () => initDispatchStatePromise,
          ...getOperationBIEventSettings(menusState, itemState, biReporterService),
          name: 'initDispatchState',
        });

        const oloLoadedPromise = Promise.all([
          operationsPromise,
          cartPromise,
          itemPromise,
          initDispatchStateWithDurationPromise,
        ]).then(async () => {
          dispatchState.setIsDataLoaded(false);
          if (!environment.isSSR) {
            if (experiments.enabled(SPECS.seo)) {
              const itemData = { images: convertMenusToImageSD(menusState.sortedMenusDto) };
              wixCodeApi.seo.renderSEOTags({ itemType: 'IMAGES_COMPONENT', itemData });
            }
            registerOnCartChangeEvent();
            await openItemModalsIfNeeded(menusState.sortedMenusDto);
          }

          const { utm_source: refferalInfo = undefined } = wixCodeApi.location.query ?? {};

          if (dispatchState.isMultiLocation) {
            const queryParamLocationId = getLocationIdByQueryParam();
            const selectedLocationId = queryParamLocationId ?? (await getLocationIdFromCart());
            if (queryParamLocationId !== selectedLocationId) {
              setQueryParamLocationId(selectedLocationId);
            }
          }

          biReporterService?.reportOloLiveSiteOloPageLoadedBiEvent({
            isMemberLoggedIn,
            menus: menusState.sortedMenusDto,
            refferalInfo,
          });

          fedopsLogger.loadOloPageEnded();
          biReporterService?.reportRestaurantsUouPageFinishedLoadingBiEvent();
          initialLoad = false;
        });
        const operations = await operationsPromise;
        operations && dispatchState.setOperations(operations);
        dispatchState.setCurrentOperationId(operationId);
        !environment.isEditor && registerToUrlChange(wixCodeApi, editCartItem);

        if (environment.isEditor && !isLazyLoadMenusOnEditor) {
          await oloLoadedPromise;
        }

        autorun(async () => {
          if (
            enableMultiLocation &&
            dispatchState.isMultiLocation &&
            dispatchState.currentOperationId
          ) {
            !initialLoad && init(dispatchState.currentOperationId);
          }
        });
      } catch (e) {
        // TODO: set menus error state
        context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
        // eslint-disable-next-line no-console
        console.log('error', e);
      }
    },
    exports: {},
  };
});
